import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import { RichTextField } from './RichTextField';
import { TooltipListInput, type TooltipItem } from './TooltipListInput';
import { cn } from '@/lib/utils';

export interface NewsSubsection {
  countries?: string[];
  content?: string;
  tooltips?: TooltipItem[];
}

interface NewsSubsectionsInputProps {
  value?: NewsSubsection[];
  onChange: (_sections: NewsSubsection[]) => void;
}

const COUNTRY_OPTIONS = [
  { value: 'LATAM', label: '🌎 LATAM' },
  { value: 'Global', label: '🌍 Global' },
  { value: 'Brasil', label: '🇧🇷 Brasil' },
  { value: 'Argentina', label: '🇦🇷 Argentina' },
  { value: 'México', label: '🇲🇽 México' },
  { value: 'Colômbia', label: '🇨🇴 Colômbia' },
  { value: 'Chile', label: '🇨🇱 Chile' },
  { value: 'Peru', label: '🇵🇪 Peru' },
  { value: 'Uruguai', label: '🇺🇾 Uruguai' },
  { value: 'Paraguai', label: '🇵🇾 Paraguai' },
  { value: 'Bolívia', label: '🇧🇴 Bolívia' },
  { value: 'Equador', label: '🇪🇨 Equador' },
  { value: 'Venezuela', label: '🇻🇪 Venezuela' },
];

const defaultSubsection = (): NewsSubsection => ({
  countries: [],
  content: '',
  tooltips: [],
});

function CountriesSelector({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (_v: string[]) => void;
}) {
  const available = COUNTRY_OPTIONS.filter((o) => !selected.includes(o.value));

  const addCountry = (value: string) => {
    if (!value || selected.includes(value)) return;
    onChange([...selected, value]);
  };

  const removeCountry = (value: string) => {
    onChange(selected.filter((c) => c !== value));
  };

  const labelFor = (value: string) =>
    COUNTRY_OPTIONS.find((o) => o.value === value)?.label ?? value;

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Países (opcional)
      </label>
      <div className="flex flex-wrap gap-2 min-h-[32px]">
        {selected.map((country) => (
          <span
            key={country}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-200 text-gray-800 text-xs font-medium"
          >
            {labelFor(country)}
            <button
              type="button"
              onClick={() => removeCountry(country)}
              className="ml-0.5 hover:text-red-600 transition-colors"
              aria-label={`Remover ${country}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {selected.length === 0 && (
          <span className="text-xs text-gray-400 self-center">Nenhum país selecionado</span>
        )}
      </div>
      {available.length > 0 && (
        <select
          value=""
          onChange={(e) => addCountry(e.target.value)}
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
        >
          <option value="" disabled>
            Adicionar país...
          </option>
          {available.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

export const NewsSubsectionsInput: React.FC<NewsSubsectionsInputProps> = ({
  value = [],
  onChange,
}) => {
  const [sections, setSections] = useState<NewsSubsection[]>(value);
  const [openIndexes, setOpenIndexes] = useState<Set<number>>(new Set([0]));

  useEffect(() => {
    setSections(value);
  }, [value]);

  const addSection = useCallback(() => {
    const next = [...sections, defaultSubsection()];
    setSections(next);
    onChange(next);
    setOpenIndexes((prev) => new Set([...prev, next.length - 1]));
  }, [sections, onChange]);

  const removeSection = useCallback(
    (index: number) => {
      const next = sections.filter((_, i) => i !== index);
      setSections(next);
      onChange(next);
      setOpenIndexes((prev) => {
        const updated = new Set<number>();
        prev.forEach((i) => {
          if (i < index) updated.add(i);
          else if (i > index) updated.add(i - 1);
        });
        return updated;
      });
    },
    [sections, onChange]
  );

  const updateSection = useCallback(
    <K extends keyof NewsSubsection>(index: number, field: K, fieldValue: NewsSubsection[K]) => {
      const next = sections.map((s, i) =>
        i === index ? { ...s, [field]: fieldValue } : s
      );
      setSections(next);
      onChange(next);
    },
    [sections, onChange]
  );

  const toggleOpen = (index: number) => {
    setOpenIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {sections.length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg">
          Nenhuma caixa de texto adicionada.
          <br />
          <span className="text-xs text-gray-400">
            Adicione uma ou mais caixas de texto com conteúdo e balões direcionais.
          </span>
        </div>
      ) : (
        sections.map((section, index) => {
          const isOpen = openIndexes.has(index);
          return (
            <div
              key={index}
              className={cn(
                'border rounded-lg overflow-hidden',
                isOpen ? 'border-gray-300' : 'border-gray-200'
              )}
            >
              <button
                type="button"
                onClick={() => toggleOpen(index)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <div className="flex items-center gap-2">
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
                  )}
                  <span className="text-sm font-semibold text-gray-700">
                    Caixa de texto {index + 1}
                  </span>
                  {section.countries && section.countries.length > 0 && (
                    <span className="text-xs text-gray-500">
                      {section.countries
                        .map(
                          (c) => COUNTRY_OPTIONS.find((o) => o.value === c)?.label ?? c
                        )
                        .join(', ')}
                    </span>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSection(index);
                  }}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </button>

              {isOpen && (
                <div className="p-4 space-y-5 bg-white">
                  <CountriesSelector
                    selected={section.countries ?? []}
                    onChange={(v) => updateSection(index, 'countries', v)}
                  />

                  <RichTextField
                    label="Conteúdo"
                    value={section.content ?? ''}
                    onChange={(v) => updateSection(index, 'content', v)}
                    placeholder="Texto da caixa de conteúdo..."
                  />

                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Balões direcionais
                    </p>
                    <TooltipListInput
                      value={section.tooltips ?? []}
                      onChange={(v) => updateSection(index, 'tooltips', v)}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addSection}
        className="w-full gap-2"
      >
        <Plus className="w-4 h-4" />
        Adicionar caixa de texto
      </Button>
    </div>
  );
};
